import React, { useEffect, useState } from 'react';
import {
  Button, Checkbox, Collapse, Divider, Drawer, Input, Spin,
  Tag,
} from 'antd';
import {
  useLocation, useNavigate, useParams, useSearchParams,
} from 'react-router-dom';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { useArticleContext } from '../context';
import SelectedFilters from './SelectedFilters';

import styles from './index.module.scss';

const { Panel } = Collapse;

interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
}

export default function Filter({ open, onClose }: FilterDrawerProps): React.ReactNode | null {
  const {
    filters, setFilters, fetch, buildQueryString, categories, subCategories, categoryLoading, tags, meta, loading, data,
    setCurrentProduct,
  } = useArticleContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { id, name } = useParams();
  const query = searchParams.get('title');
  const totalResults = meta?.itemCount ?? 0;
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [showAllSubCategories, setShowAllSubCategories] = useState(false);
  const categoriesToShow = showAllCategories ? categories : categories?.slice(0, 5);
  const subCategoriesToShow = showAllSubCategories ? subCategories : subCategories?.slice(0, 5);
  const [showButton, setShowButton] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);

  const location = useLocation();

  const hasSelectedFilters = Boolean(
    filters?.categoryId?.length
    || filters?.subCategoryId?.length
    || filters?.types?.length
    || filters?.tags?.length,
  );

  useEffect(() => {
    const isSearchResultsPage = location.pathname.includes('/search-results');

    if (!isSearchResultsPage) {
      setFilters?.({
        categoryId: [],
        subCategoryId: [],
        types: [],
        tags: [],
      });
    }
  }, [location.pathname]);

  useEffect(() => {
    const hasFilters = Boolean(
      query
      || filters?.categoryId?.length
      || filters?.subCategoryId?.length
      || filters?.types?.length
      || filters?.tags?.length,
    );

    if (hasFilters && !window.location.pathname.includes('/search-results')) {
      fetch?.({
        title: query || '',
        page: '1',
        categoryId: filters?.categoryId,
        subCategoryId: filters?.subCategoryId,
        types: filters?.types,
        tags: filters?.tags,
        // productId: id,
      });
    }
  }, [filters, query, id]);
  useEffect(() => {
    if (id && name) {
      setCurrentProduct?.({ id, name: decodeURIComponent(name) });
    }
  }, [id, name]);

  useEffect(() => {
    const hasValidData = !loading && (data?.length ?? 0) > 0 && (meta?.itemCount ?? 0) > 0;

    if (hasValidData) {
      setLoadedOnce(true);
    }
  }, [loading, data, meta]);

  useEffect(() => {
    setLoadedOnce(false);
  }, [filters, query]);

  useEffect(() => {
    setShowButton(hasSelectedFilters && loadedOnce);
  }, [hasSelectedFilters, loadedOnce]);

  return (
    <div className={styles.filterWrapper}>
      <Drawer
        placement="right"
        onClose={onClose}
        open={open}
        width={480}
        closable={false}
        bodyStyle={{ paddingBottom: 80 }}
        headerStyle={{ display: 'none' }}
        className={styles.filter}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            marginBottom: 24,
          }}
          className={styles.filterTitle}
        >
          <h2>Filters</h2>
          <div className={styles.headerBtns}>
            {hasSelectedFilters && (
              <div
                role="button"
                tabIndex={-1}
                className={styles.clearBnt}
                onClick={() => {
                  setFilters?.({
                    categoryId: [],
                    subCategoryId: [],
                    types: [],
                    tags: [],
                  });
                }}
              >
                Clear all
              </div>
            )}
            <Button
              className={styles.btn}
              type="text"
              icon={<span style={{ fontSize: 24 }}>×</span>}
              onClick={onClose}
            />
          </div>
        </div>
        <Divider />
        <div>
          <SelectedFilters isFilter />
        </div>
        <Collapse
          defaultActiveKey={['categories', 'subcategories', 'type', 'tags']}
          className={styles.collapse}
          // eslint-disable-next-line react/no-unstable-nested-components
          expandIcon={({ isActive }) => (
            isActive ? <UpOutlined style={{ color: '#1F384766' }} /> : <DownOutlined style={{ color: '#1F384766' }} />
          )}
        >
          <Panel key="categories" header={<span className={styles.panelHeader}>Category</span>}>
            {categoryLoading ? <Spin /> : (
              <>
                <Checkbox.Group
                  value={filters?.categoryId}
                  style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
                  onChange={(checkedValues) => {
                    setFilters?.({ ...filters, categoryId: checkedValues as string[] });
                  }}
                >
                  {(categoriesToShow ?? []).map((cat) => (
                    <Checkbox key={cat.id} value={cat.id} className={styles.categoryItem}>
                      {cat.name}
                      {/* <span style={{ color: '#1F3847AD', marginLeft: 4 }}>
                        (
                        {cat.articlesSize || '0'}
                        )
                      </span> */}
                    </Checkbox>
                  ))}
                </Checkbox.Group>
                {(categories?.length ?? 0) > 5 && (
                  <Button
                    type="link"
                    className={styles.showMore}
                    onClick={() => setShowAllCategories(!showAllCategories)}
                  >
                    {showAllCategories ? 'Show less' : 'Show more'}
                  </Button>
                )}
              </>
            )}
          </Panel>
          {subCategories?.length
            ? (
              <Panel key="subcategories" header={<span className={styles.panelHeader}>Sub category</span>}>
                {categoryLoading ? <Spin /> : (
                  <>
                    <Checkbox.Group
                      value={filters?.subCategoryId}
                      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
                      onChange={(checkedValues) => {
                        setFilters?.({ ...filters, subCategoryId: checkedValues as string[] });
                      }}
                    >
                      {(subCategoriesToShow ?? []).map((sub) => (
                        <Checkbox key={sub.id} value={sub.id} className={styles.categoryItem}>
                          {sub.name}
                          <span style={{ color: '#1F3847AD', marginLeft: 4 }}>
                            (
                            {sub.articlesSize || '0'}
                            )
                          </span>
                        </Checkbox>
                      ))}
                    </Checkbox.Group>
                    {(subCategories?.length ?? 0) > 5 && (
                      <Button
                        type="link"
                        className={styles.showMore}
                        onClick={() => setShowAllSubCategories(!showAllSubCategories)}
                      >
                        {showAllSubCategories ? 'Show less' : 'Show more'}
                      </Button>
                    )}
                  </>
                )}
              </Panel>
            )
            : null}
          <Panel
            key="type"
            header={(
              <span
                className={styles.panelHeader}
              >
                Type
              </span>
            )}
          >
            <div className={styles.types}>
              {['text', 'video', 'image', 'file'].map((type) => {
                const isActive = filters?.types?.includes(type);

                return (
                  <Button
                    key={type}
                    type="primary"
                    style={isActive ? { backgroundColor: '#00ADB9', color: '#FFFFFF' } : {}}
                    className={styles.btn}
                    onClick={() => {
                      const current = filters?.types ?? [];
                      const updated = isActive
                        ? current.filter((t) => t !== type)
                        : [...current, type];

                      setFilters?.({
                        ...filters,
                        types: updated,
                      });
                    }}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </Button>
                );
              })}
            </div>
          </Panel>
          {tags?.length
            ? (
              <Panel
                key="tags"
                header={(
                  <span
                    className={styles.panelHeader}
                  >
                    Tag
                  </span>
                )}
              >
                <div className={styles.types} style={{ marginBottom: 32 }}>
                  {(tags ?? []).map((tag) => {
                    const isActive = filters?.tags?.includes(tag);

                    return (
                      <Button
                        key={tag}
                        type="primary"
                        style={isActive ? { backgroundColor: '#00ADB9', color: '#FFFFFF' } : {}}
                        className={styles.btn}
                        onClick={() => {
                          const current = filters?.tags ?? [];
                          const updated = isActive
                            ? current.filter((t) => t !== tag)
                            : [...current, tag];

                          setFilters?.({
                            ...filters,
                            tags: updated,
                          });
                        }}
                      >
                        {tag}
                      </Button>
                    );
                  })}
                </div>
              </Panel>
            )
            : null}
        </Collapse>
        {showButton && !loading && (
          <div className={styles.results}>
            <Button
              type="primary"
              className={styles.btn}
              onClick={() => {
                navigate(`/search-results?${buildQueryString?.(query ?? '')}`);
                onClose();
              }}
            >
              {`Show ${totalResults} results`}
            </Button>
          </div>
        )}

      </Drawer>
    </div>
  );
}
